import {
  type FlashcardAdminCardNoteSummary,
  type FlashcardAdminStudentNoteSummary,
  FlashcardsService,
  ProgramsService,
} from "@/client"
import { AppEmptyState } from "@/components/Common/AppEmptyState"
import { FlashcardContent } from "@/components/Flashcards/FlashcardContent"
import { PageShell, PageSkeleton } from "@/components/Layout"
import { InputGroup } from "@/components/ui/input-group"
import {
  PaginationItems,
  PaginationNextTrigger,
  PaginationPrevTrigger,
  PaginationRoot,
} from "@/components/ui/pagination"
import { useDebounce } from "@/hooks/useDebounce"
import { useRouteBreadcrumbs } from "@/navigation/route-meta"
import {
  Badge,
  Box,
  Button,
  Card,
  HStack,
  Input,
  NativeSelectField,
  NativeSelectRoot,
  Stack,
  Table,
  Tabs,
  Text,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useMemo, useState } from "react"
import { FaSearch } from "react-icons/fa"

const PER_PAGE = 50

export const Route = createFileRoute("/_layout/flashcards/notes/")({
  component: FlashcardStudentNotesPage,
})

function FlashcardStudentNotesPage() {
  const navigate = useNavigate()
  const breadcrumbs = useRouteBreadcrumbs("flashcards.index", [
    { label: "Student Notes" },
  ])
  const [selectedProgramId, setSelectedProgramId] = useState("")
  const [view, setView] = useState<"student" | "card">("student")
  const [page, setPage] = useState(1)
  const [searchTerm, setSearchTerm] = useState("")
  const debouncedSearch = useDebounce(searchTerm, 300)

  const { data: programsData, isLoading: isProgramsLoading } = useQuery({
    queryKey: ["programs"],
    queryFn: () => ProgramsService.listPrograms({ limit: 200 }),
  })

  const { data: studentsData, isLoading: isStudentsLoading } = useQuery({
    queryKey: [
      "flashcards-admin-notes",
      "students",
      selectedProgramId,
      page,
      debouncedSearch,
    ],
    queryFn: () =>
      FlashcardsService.listProgramNoteStudents({
        programId: selectedProgramId,
        offset: (page - 1) * PER_PAGE,
        limit: PER_PAGE,
        search: debouncedSearch || undefined,
      }),
    enabled: Boolean(selectedProgramId) && view === "student",
  })

  const { data: cardsData, isLoading: isCardsLoading } = useQuery({
    queryKey: ["flashcards-admin-notes", "cards", selectedProgramId, page],
    queryFn: () =>
      FlashcardsService.listProgramNoteCards({
        programId: selectedProgramId,
        offset: (page - 1) * PER_PAGE,
        limit: PER_PAGE,
      }),
    enabled: Boolean(selectedProgramId) && view === "card",
  })

  const programs = programsData?.data ?? []
  const selectedProgram = useMemo(
    () => programs.find((program) => program.id === selectedProgramId) ?? null,
    [programs, selectedProgramId],
  )

  const handleProgramChange = (programId: string) => {
    setSelectedProgramId(programId)
    setSearchTerm("")
    setPage(1)
  }

  const handleViewChange = (nextView: "student" | "card") => {
    setView(nextView)
    setPage(1)
  }

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value)
    setPage(1)
  }

  const isViewLoading = view === "student" ? isStudentsLoading : isCardsLoading
  const activeCount =
    view === "student" ? studentsData?.count ?? 0 : cardsData?.count ?? 0

  return (
    <PageShell
      title="Student Notes"
      description="Review saved flashcards and note activity by program."
      breadcrumbs={breadcrumbs}
      py={{ base: 5, md: 8 }}
      contentGap={5}
    >
      <Card.Root>
        <Card.Body>
          <Stack gap={3}>
            <Box maxW="360px">
              <Text fontSize="sm" fontWeight="medium" mb={2}>
                Program
              </Text>
              <NativeSelectRoot>
                <NativeSelectField
                  aria-label="Program"
                  value={selectedProgramId}
                  onChange={(event) => handleProgramChange(event.target.value)}
                >
                  <option value="">Select a program</option>
                  {programs.map((program) => (
                    <option key={program.id} value={program.id}>
                      {program.name}
                    </option>
                  ))}
                </NativeSelectField>
              </NativeSelectRoot>
            </Box>
            <Text fontSize="sm" color="fg.muted">
              Choose a program to see students who have saved cards or notes.
            </Text>
          </Stack>
        </Card.Body>
      </Card.Root>

      {!selectedProgramId ? (
        <AppEmptyState
          title="Select a program"
          description="Student notes are scoped by program first."
          variant="compact"
        />
      ) : (
        <>
          <Tabs.Root
            value={view}
            onValueChange={({ value }) =>
              handleViewChange((value as "student" | "card") ?? "student")
            }
            variant="subtle"
          >
            <Tabs.List>
              <Tabs.Trigger value="student">By student</Tabs.Trigger>
              <Tabs.Trigger value="card">By card</Tabs.Trigger>
            </Tabs.List>
          </Tabs.Root>

          {view === "student" ? (
            <Box maxW="md">
              <InputGroup startElement={<FaSearch />} w="full">
                <Input
                  aria-label="Search students"
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </InputGroup>
            </Box>
          ) : null}

          {isProgramsLoading || isViewLoading ? (
            <PageSkeleton variant="table" rows={5} />
          ) : view === "student" ? (
            (studentsData?.data?.length ?? 0) === 0 ? (
              <AppEmptyState
                title={
                  debouncedSearch
                    ? "No matching students"
                    : "No saved flashcards in this program"
                }
                description={
                  debouncedSearch
                    ? "No students match your search."
                    : "Students in this program have not bookmarked cards or added notes yet."
                }
                variant="compact"
              />
            ) : (
              <Card.Root>
                <Table.Root size="sm">
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeader>Student</Table.ColumnHeader>
                      <Table.ColumnHeader>Email</Table.ColumnHeader>
                      <Table.ColumnHeader textAlign="right">
                        Saved Cards
                      </Table.ColumnHeader>
                      <Table.ColumnHeader textAlign="right">
                        Notes
                      </Table.ColumnHeader>
                      <Table.ColumnHeader />
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {(studentsData?.data ?? []).map((student) => (
                      <StudentSummaryRow
                        key={student.student_id}
                        student={student}
                        onOpen={() =>
                          navigate({
                            to: "/flashcards/notes/$studentId",
                            params: { studentId: student.student_id },
                            search: { programId: selectedProgramId },
                          })
                        }
                      />
                    ))}
                  </Table.Body>
                </Table.Root>
              </Card.Root>
            )
          ) : (cardsData?.data?.length ?? 0) === 0 ? (
            <AppEmptyState
              title="No saved cards in this program"
              description="No cards in this program have been bookmarked or annotated yet."
              variant="compact"
            />
          ) : (
            <Card.Root>
              <Table.Root size="sm">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader>Card</Table.ColumnHeader>
                    <Table.ColumnHeader>Deck</Table.ColumnHeader>
                    <Table.ColumnHeader textAlign="right">
                      Saved
                    </Table.ColumnHeader>
                    <Table.ColumnHeader textAlign="right">
                      Notes
                    </Table.ColumnHeader>
                    <Table.ColumnHeader />
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {(cardsData?.data ?? []).map((card) => (
                    <CardSummaryRow
                      key={card.card_id}
                      card={card}
                      onOpen={() =>
                        navigate({
                          to: "/flashcards/notes/cards/$cardId",
                          params: { cardId: card.card_id },
                          search: { programId: selectedProgramId },
                        })
                      }
                    />
                  ))}
                </Table.Body>
              </Table.Root>
            </Card.Root>
          )}

          {activeCount > PER_PAGE && (
            <PaginationRoot
              count={activeCount}
              pageSize={PER_PAGE}
              page={page}
              onPageChange={(e) => setPage(e.page)}
            >
              <HStack justify="center" gap={2}>
                <PaginationPrevTrigger />
                <PaginationItems />
                <PaginationNextTrigger />
              </HStack>
            </PaginationRoot>
          )}
        </>
      )}

      {selectedProgram ? (
        <Text fontSize="sm" color="fg.muted">
          Viewing note activity for {selectedProgram.name}.
        </Text>
      ) : null}
    </PageShell>
  )
}

function StudentSummaryRow({
  student,
  onOpen,
}: {
  student: FlashcardAdminStudentNoteSummary
  onOpen: () => void
}) {
  return (
    <Table.Row>
      <Table.Cell>{student.full_name || "Unnamed student"}</Table.Cell>
      <Table.Cell>{student.email}</Table.Cell>
      <Table.Cell textAlign="right">{student.saved_count}</Table.Cell>
      <Table.Cell textAlign="right">{student.note_count}</Table.Cell>
      <Table.Cell textAlign="right">
        <Button size="sm" variant="outline" onClick={onOpen}>
          View notes
        </Button>
      </Table.Cell>
    </Table.Row>
  )
}

function CardSummaryRow({
  card,
  onOpen,
}: {
  card: FlashcardAdminCardNoteSummary
  onOpen: () => void
}) {
  return (
    <Table.Row>
      <Table.Cell>
        <Box lineClamp={2}>
          <FlashcardContent content={card.front} />
        </Box>
      </Table.Cell>
      <Table.Cell>
        <Badge colorPalette="teal">{card.deck_name}</Badge>
      </Table.Cell>
      <Table.Cell textAlign="right">{card.saved_count}</Table.Cell>
      <Table.Cell textAlign="right">{card.note_count}</Table.Cell>
      <Table.Cell textAlign="right">
        <Button size="sm" variant="outline" onClick={onOpen}>
          View
        </Button>
      </Table.Cell>
    </Table.Row>
  )
}

export default FlashcardStudentNotesPage
